export type ConversationRecencyGroup = 'today' | 'yesterday' | 'last7days' | 'older';

const recencyGroupOrder: ConversationRecencyGroup[] = ['today', 'yesterday', 'last7days', 'older'];

function startOfDay(date: Date): Date {
    const day = new Date(date);
    day.setHours(0, 0, 0, 0);
    return day;
}

/** Which history bucket a conversation's `updatedAt` falls into, calendar-day based. */
export function conversationRecencyGroup(updatedAt: Date, now: Date): ConversationRecencyGroup {
    const dayMs = 24 * 60 * 60 * 1000;
    const daysAgo = Math.round((startOfDay(now).getTime() - startOfDay(updatedAt).getTime()) / dayMs);

    if (daysAgo <= 0) {
        return 'today';
    }
    if (daysAgo === 1) {
        return 'yesterday';
    }
    if (daysAgo < 7) {
        return 'last7days';
    }
    return 'older';
}

/**
 * Buckets conversations (already sorted most-recent first) into the history
 * view's recency groups, keeping order within each. Empty groups are omitted.
 */
export function groupConversationsByRecency<T extends { updatedAt: Date }>(
    conversations: T[],
    now: Date,
): { group: ConversationRecencyGroup; items: T[] }[] {
    const buckets = new Map<ConversationRecencyGroup, T[]>();
    for (const conversation of conversations) {
        const group = conversationRecencyGroup(conversation.updatedAt, now);
        const items = buckets.get(group);
        if (items) {
            items.push(conversation);
        } else {
            buckets.set(group, [conversation]);
        }
    }

    return recencyGroupOrder
        .filter((group) => buckets.has(group))
        .map((group) => ({ group, items: buckets.get(group) ?? [] }));
}

/**
 * The row's right-aligned timestamp: a time for today and yesterday, a short
 * weekday within the week, a short date beyond it.
 */
export function conversationTimestampLabel(updatedAt: Date, now: Date, locale: string): string {
    const group = conversationRecencyGroup(updatedAt, now);
    if (group === 'today' || group === 'yesterday') {
        return updatedAt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    }
    if (group === 'last7days') {
        return updatedAt.toLocaleDateString(locale, { weekday: 'short' });
    }
    return updatedAt.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}
